import { useLayoutEffect, useRef, useState } from "react";

/**
 * Reserves table height from measured metrics — header row plus `rowCount` times
 * a hidden probe row — so a short result keeps the card's height without padding
 * the body with placeholder rows. Stays `0` until a real measurement lands
 * (before first paint, or in jsdom), so the caller falls back to natural height
 * instead of collapsing to a bogus reserve.
 */
export const useReservedRowsHeight = (rowCount: number) => {
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const probeRowRef = useRef<HTMLTableRowElement>(null);
  const [metrics, setMetrics] = useState({
    headerHeight: 0,
    reservedHeight: 0,
  });

  useLayoutEffect(() => {
    const measure = () => {
      const rowHeight =
        probeRowRef.current?.getBoundingClientRect().height ?? 0;
      if (rowHeight <= 0) {
        return;
      }
      const headerHeight =
        headerRowRef.current?.getBoundingClientRect().height ?? 0;
      const reservedHeight = headerHeight + rowHeight * rowCount;
      setMetrics((current) =>
        Math.abs(current.reservedHeight - reservedHeight) < 1 &&
        Math.abs(current.headerHeight - headerHeight) < 1
          ? current
          : { headerHeight, reservedHeight },
      );
    };

    measure();

    const probe = probeRowRef.current;
    if (!probe || typeof ResizeObserver === "undefined") {
      return;
    }
    // Row metrics move with font loading and the Radix scale, not with data.
    const observer = new ResizeObserver(measure);
    observer.observe(probe);
    if (headerRowRef.current) {
      observer.observe(headerRowRef.current);
    }
    return () => observer.disconnect();
  }, [rowCount]);

  return { headerRowRef, probeRowRef, ...metrics };
};
